import { useEffect, useState, useMemo } from "react";
import api, { formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import { DollarSign, Trash2, Plus, Calendar, Landmark, TrendingDown, Target } from "lucide-react";
import LoanPlanCard from "./loans/LoanPlanCard";
import USDateInput from "./USDateInput";

const money = (n) => "$" + (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
const money2 = (n) => "$" + (Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Determine whether `payment_date` falls inside the IO window for this loan.
// Mirrors the backend check in server.py `create_loan_payment` so the UI
// default matches what the backend would compute.
function isInIoPeriod(loan, paymentDateStr) {
  if (!loan || loan.loan_type !== "interest_only") return false;
  const io = Number(loan.io_period_months || 0);
  if (io === 0) return true;            // full-term IO
  const ld = loan.loan_date;
  if (!ld || !paymentDateStr) return false;
  const [ly, lm] = ld.split("-").map(Number);
  const [py, pm] = paymentDateStr.split("-").map(Number);
  if (!ly || !py) return false;
  const monthsElapsed = (py - ly) * 12 + (pm - lm);
  return monthsElapsed < io;
}

export default function LoanPaymentsDialog({ loan, open, onOpenChange }) {
  const [summary, setSummary] = useState(null);
  const [plan, setPlan] = useState(null);
  const [form, setForm] = useState({
    payment_date: new Date().toISOString().slice(0, 10),
    amount: "",
    notes: "",
    // "pi"            → backend default split (P&I amortizing math)
    // "interest_only" → whole payment goes to interest, principal untouched
    allocation_mode: "pi",
  });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!loan) return;
    try {
      const [{ data: acc }, { data: planData }] = await Promise.all([
        api.get(`/loans/${loan.id}/accounting`),
        api.get(`/loans/${loan.id}/plan`),
      ]);
      setSummary(acc);
      setPlan(planData?.plan ? planData : null);
      // Prefill amount with scheduled monthly if not editing
      setForm((f) => ({
        ...f,
        amount: f.amount || (acc.scheduled_monthly || ""),
        // Default allocation: "interest_only" when the payment date is inside
        // the IO period of an interest_only loan, else "pi".
        allocation_mode: isInIoPeriod(acc.loan, f.payment_date) ? "interest_only" : "pi",
      }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Load failed");
    }
  };
  useEffect(() => { if (open && loan) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [open, loan?.id]);

  // Re-evaluate default allocation when the user changes the payment date —
  // helpful for loans whose payment crosses the IO boundary.
  useEffect(() => {
    if (!summary?.loan) return;
    const shouldBeIO = isInIoPeriod(summary.loan, form.payment_date);
    // Only auto-switch if the user hasn't manually overridden the mode from
    // the computed default for this loan.
    setForm((f) => ({
      ...f,
      allocation_mode: shouldBeIO ? "interest_only" : "pi",
    }));
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [form.payment_date, summary?.loan?.id]);

  // Live preview of how the server will split this payment.
  const preview = useMemo(() => {
    if (!summary || !form.amount) return null;
    const amount = Number(form.amount) || 0;
    if (amount <= 0) return null;
    const balance = summary.totals.remaining ?? 0;
    const monthlyRate = (Number(summary.loan?.interest_rate) || 0) / 100 / 12;
    const interestDue = Math.round(balance * monthlyRate * 100) / 100;
    if (form.allocation_mode === "interest_only") {
      return { principal: 0, interest: Math.round(amount * 100) / 100, interest_due: interestDue };
    }
    const interest = Math.min(interestDue, amount);
    const principal = Math.max(amount - interest, 0);
    return {
      principal: Math.round(principal * 100) / 100,
      interest: Math.round(interest * 100) / 100,
      interest_due: interestDue,
    };
  }, [form.amount, form.allocation_mode, summary]);

  const addPayment = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {
        payment_date: form.payment_date,
        amount: Number(form.amount),
        notes: form.notes,
      };
      // For "interest_only" mode, send explicit principal=0 / interest=amount
      // so the backend records the split exactly as the UI preview shows it.
      // For "pi" mode, omit the split and let the backend compute it using
      // interest_due = balance × rate (the default amortizing behavior).
      if (form.allocation_mode === "interest_only") {
        body.principal = 0;
        body.interest = Number(form.amount);
      }
      await api.post(`/loans/${loan.id}/payments`, body);
      toast.success("Payment recorded");
      setForm((f) => ({
        payment_date: new Date().toISOString().slice(0, 10),
        amount: summary?.scheduled_monthly || "",
        notes: "",
        allocation_mode: f.allocation_mode,
      }));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  const removePayment = async (pid) => {
    if (!window.confirm("Delete this payment? This will restore principal to the loan balance.")) return;
    try {
      await api.delete(`/loans/${loan.id}/payments/${pid}`);
      toast.success("Payment deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  const removePlan = async () => {
    if (!window.confirm("Remove the target payoff plan? Your payment history stays intact.")) return;
    try {
      await api.delete(`/loans/${loan.id}/plan`);
      toast.success("Plan removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not remove plan");
    }
  };

  const paidPct = summary?.totals.principal_pct ?? 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto p-6">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }}>
            Loan Payments & Accounting
            {loan && <span className="block text-xs font-normal text-slate-500 mt-1 font-mono">
              {loan.loan_number} · {loan.property_address}
            </span>}
          </DialogTitle>
        </DialogHeader>

        {summary && (
          <>
            {/* KPI strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4" data-testid="loan-accounting-kpis">
              <Kpi icon={Landmark}      label="Original"   value={money(summary.loan.loan_amount)} />
              <Kpi icon={TrendingDown}  label="Remaining"  value={money(summary.totals.remaining)} accent="cyan" />
              <Kpi icon={DollarSign}    label="Principal Paid" value={money(summary.totals.principal)} accent="emerald" sub={`${paidPct}%`} />
              <Kpi icon={DollarSign}    label="Interest Paid"  value={money(summary.totals.interest)}  accent="amber" />
            </div>

            {/* Progress bar */}
            <div className="mt-4" data-testid="loan-paydown-bar">
              <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-1.5">
                <span>Loan pay-down</span>
                <span>{paidPct}% of principal · {summary.totals.payments_count} payment{summary.totals.payments_count === 1 ? "" : "s"}</span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500 transition-all duration-700" style={{ width: `${Math.max(2, Math.min(100, paidPct))}%` }} />
              </div>
            </div>

            {/* Target plan card */}
            {plan?.plan && <LoanPlanCard plan={plan.plan} progress={plan.progress} onRemove={removePlan} />}

            {/* Add payment form */}
            <form onSubmit={addPayment} className="mt-5 bg-slate-50 border border-slate-200 rounded-sm p-4">
              <div className="overline text-slate-500 mb-3 flex items-center gap-2">
                <Plus className="w-3.5 h-3.5" /> Record payment
                {summary.next_due && <span className="text-[10px] text-slate-400 ml-auto font-mono">next due {summary.next_due}</span>}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                <label className="block">
                  <div className="overline text-slate-600 mb-1">Date</div>
                  <USDateInput required data-testid="payment-date-input" value={form.payment_date} onChange={(iso) => setForm({ ...form, payment_date: iso })} />
                </label>
                <label className="block">
                  <div className="overline text-slate-600 mb-1">Amount ($)</div>
                  <input type="number" step="0.01" required className="input-clean w-full" data-testid="payment-amount-input" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder={String(summary.scheduled_monthly || "")} />
                </label>
                <label className="block">
                  <div className="overline text-slate-600 mb-1">Notes</div>
                  <input className="input-clean w-full" data-testid="payment-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="optional" />
                </label>
              </div>
              <div className="mt-3">
                <div className="overline text-slate-600 mb-1.5">Apply payment as</div>
                <div className="inline-flex rounded-sm border border-slate-300 overflow-hidden" role="group" data-testid="payment-allocation-group">
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, allocation_mode: "pi" })}
                    className={`px-4 py-1.5 text-xs font-semibold transition ${
                      form.allocation_mode === "pi"
                        ? "bg-slate-900 text-white"
                        : "bg-white text-slate-700 hover:bg-slate-50"
                    }`}
                    data-testid="payment-alloc-pi"
                  >
                    Principal &amp; Interest
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, allocation_mode: "interest_only" })}
                    className={`px-4 py-1.5 text-xs font-semibold border-l border-slate-300 transition ${
                      form.allocation_mode === "interest_only"
                        ? "bg-slate-900 text-white"
                        : "bg-white text-slate-700 hover:bg-slate-50"
                    }`}
                    data-testid="payment-alloc-io"
                  >
                    Interest Only
                  </button>
                </div>
                {preview && (
                  <div
                    className="mt-2 text-[11px] font-mono text-slate-600 flex items-center gap-3 flex-wrap"
                    data-testid="payment-alloc-preview"
                  >
                    <span>
                      Will record:
                      <span className="text-emerald-700 font-bold mx-1" data-testid="payment-preview-principal">
                        {money2(preview.principal)}
                      </span>
                      principal •
                      <span className="text-amber-700 font-bold mx-1" data-testid="payment-preview-interest">
                        {money2(preview.interest)}
                      </span>
                      interest
                    </span>
                    {form.allocation_mode === "pi" && preview.interest_due > 0 && (
                      <span className="text-slate-400">
                        (interest due this period: {money2(preview.interest_due)})
                      </span>
                    )}
                    {form.allocation_mode === "interest_only" &&
                      summary?.loan?.loan_type !== "interest_only" && (
                        <span className="text-amber-600">
                          · principal will NOT be reduced
                        </span>
                      )}
                  </div>
                )}
              </div>

              <div className="mt-3 flex items-center justify-between">
                <div className="text-[11px] text-slate-500">
                  {form.allocation_mode === "interest_only"
                    ? "Entire payment applied to interest — balance unchanged."
                    : "Principal/interest split auto-calculated from current balance & rate."}
                </div>
                <button type="submit" disabled={busy} className="btn-primary-dark text-sm px-5 py-2 disabled:opacity-50" data-testid="payment-submit-button">
                  {busy ? "Recording…" : "Record payment"}
                </button>
              </div>
            </form>

            {/* Payment history */}
            <div className="mt-6">
              <div className="flex items-center justify-between mb-2">
                <div className="overline text-slate-500 flex items-center gap-2"><Calendar className="w-3.5 h-3.5" /> Payment history</div>
                <div className="text-[11px] text-slate-400 font-mono">scheduled {money2(summary.scheduled_monthly)}/mo</div>
              </div>
              {summary.payments.length === 0 ? (
                <div className="text-center py-8 text-slate-400 text-sm border border-dashed border-slate-200 rounded-sm">No payments recorded yet.</div>
              ) : (
                <div className="border border-slate-200 rounded-sm overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr className="text-left">
                        <th className="px-4 py-2.5 overline text-slate-600">Date</th>
                        <th className="px-4 py-2.5 overline text-slate-600 text-right">Amount</th>
                        <th className="px-4 py-2.5 overline text-slate-600 text-right">Principal</th>
                        <th className="px-4 py-2.5 overline text-slate-600 text-right">Interest</th>
                        <th className="px-4 py-2.5 overline text-slate-600 text-right">Balance After</th>
                        <th className="px-4 py-2.5 overline text-slate-600"></th>
                      </tr>
                    </thead>
                    <tbody data-testid="payment-history-tbody">
                      {summary.payments.map((p) => (
                        <tr key={p.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50" data-testid={`payment-row-${p.id}`}>
                          <td className="px-4 py-2.5 font-mono text-xs">{p.payment_date}</td>
                          <td className="px-4 py-2.5 text-right font-mono">{money2(p.amount)}</td>
                          <td className="px-4 py-2.5 text-right font-mono text-emerald-700">{money2(p.principal)}</td>
                          <td className="px-4 py-2.5 text-right font-mono text-amber-700">{money2(p.interest)}</td>
                          <td className="px-4 py-2.5 text-right font-mono">{money2(p.balance_after)}</td>
                          <td className="px-4 py-2.5 text-right">
                            <button onClick={() => removePayment(p.id)} className="text-slate-400 hover:text-red-600 p-1" title="Delete payment" data-testid={`payment-delete-${p.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Kpi({ icon: Icon, label, value, sub, accent }) {
  const cls = accent === "emerald" ? "text-emerald-600" : accent === "amber" ? "text-amber-600" : accent === "cyan" ? "text-cyan-700" : "text-slate-900";
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-3">
      <div className="overline text-slate-500 flex items-center gap-1.5"><Icon className="w-3.5 h-3.5" /> {label}</div>
      <div className={`mt-1 text-xl font-bold font-mono tracking-tight ${cls}`}>{value}</div>
      {sub && <div className="text-[10px] text-slate-400 font-mono uppercase tracking-widest mt-0.5">{sub}</div>}
    </div>
  );
}

